import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAppContext } from 'Context/ReactAppContext';
import { useDebounce } from 'Src/hooks/use-debounce/useDebounce';
import {
  getBlockInputDepartureDate,
  getCalendarDepartureCity,
  getCalendarLoading,
  getCalendarProductId,
} from 'Store/calendar/calendar.selector';
import { getPriceOfStay } from 'Store/price-remote/price-of-stay/priceOfStay.actions';
import { getDepartureDate, getTripDuration } from 'Store/trip-criteria/tripCriteria.selector';
import type { Adults } from 'Store/trip-information/adults/adults.reducer';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import { getAdults, getKids } from 'Store/trip-information/storeHelper';
import { getPriceOfStayArgs } from 'Store/price-remote/price-of-stay/priceOfStay.helper';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import {
  priceOfStayArgsSelector,
  priceOfStayIsLoadingSelector,
  priceOfStaySelector,
} from 'Store/price-remote/price-of-stay/priceOfStay.selector';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
export function usePriceOfStay(productId: string, departureCity: string) {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const { isAppMounted } = useResponsive();
  const {
    query: { offer: offerName },
  } = useRouter();
  const adults: ReadonlyArray<Adults> = useSelector(getAdults);
  const blockInputDepartureDate = useSelector(getBlockInputDepartureDate);
  const calendarIsLoading = useSelector<boolean>(getCalendarLoading);
  const calendarDepartureCity = useSelector(getCalendarDepartureCity);
  const calendarProductId = useSelector(getCalendarProductId);
  const departureDate: number = useSelector(getDepartureDate);
  const duration: number = useSelector(getTripDuration);
  const kids: ReadonlyArray<Kids> = useSelector(getKids);
  const priceOfStayArgs = useSelector(priceOfStayArgsSelector);
  const priceOfStayIsLoading = useSelector(priceOfStayIsLoadingSelector);
  const { priceOfStay, priceOfStayError, priceTicketIsLoading } = useSelector((state) => ({
    priceOfStay: priceOfStaySelector(state),
    priceOfStayError: state.priceRemote.priceOfStay.error,
    priceTicketIsLoading: state.priceTicket.isLoading,
  }));
  const calendarSynchronized =
    calendarDepartureCity === departureCity && calendarProductId === productId;
  const calendarIsReady = calendarSynchronized && !calendarIsLoading;
  const hasValidCriteria = !blockInputDepartureDate && !!departureDate && !!duration;
  const withCustomCriteria = hasValidCriteria && calendarIsReady;
  const searchCriteria = {
    productId,
    departureDate: withCustomCriteria ? departureDate : null,
    departureCity,
    duration,
    ...(kids && withCustomCriteria
      ? {
          kids,
        }
      : null),
    ...(adults && withCustomCriteria
      ? {
          adults,
        }
      : null),
    offerName,
  };
  // Fixme: refactor kids and adults reducers so that their state does not change unnecessarily
  const debouncedSearchCriteria = useDebounce(
    [
      departureCity,
      withCustomCriteria ? departureDate : null,
      duration,
      JSON.stringify(withCustomCriteria ? kids : null),
      JSON.stringify(withCustomCriteria ? adults : null),
      productId,
    ],
    500,
  );
  useEffect(() => {
    if (withCustomCriteria && departureCity) {
      dispatch(getPriceOfStay(appContext, searchCriteria));
    }
  }, debouncedSearchCriteria);
  const searchCriteriaNotSynchronized =
    withCustomCriteria &&
    JSON.stringify(priceOfStayArgs) !== JSON.stringify(getPriceOfStayArgs(searchCriteria));
  const isLoading =
    !isAppMounted ||
    !calendarIsReady ||
    searchCriteriaNotSynchronized ||
    (isFeatureFlipped(appContext)('remoteBasePriceDisplay') && priceTicketIsLoading) ||
    priceOfStayIsLoading;
  const hasValidPriceTicketCriteria = departureCity && productId;
  const shouldDisplayPriceTicket = isFeatureFlipped(appContext)('remoteBasePriceDisplay');
  const shouldDisplaySpinnerForPriceTicket =
    shouldDisplayPriceTicket &&
    hasValidPriceTicketCriteria &&
    (!calendarSynchronized || priceTicketIsLoading);
  const shouldDisplaySpinnerForPriceOfStay =
    !shouldDisplayPriceTicket &&
    hasValidCriteria &&
    (!calendarSynchronized || priceOfStayIsLoading);
  const shouldDisplaySpinner =
    searchCriteriaNotSynchronized ||
    shouldDisplaySpinnerForPriceTicket ||
    shouldDisplaySpinnerForPriceOfStay;
  return {
    hasValidCriteria,
    isLoading,
    priceOfStay,
    priceOfStayError,
    shouldDisplaySpinner,
  };
}
